import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { MyAccount } from '../../../../../../../models/my-account';
import { OrganizationConfigService } from '../../../../../../../services/organization-config/organization-config.service';
import { AccountService } from '../../../../../../../services/account/account.service';
import { SimpleMessageModalComponent } from '../../../../../../../components/modals/simple-message-modal/simple-message-modal.component';
import { ComponentHasUnsavedChanges } from '../../../../../../../unsaved-changes-guard.service';
import { EntryPass, EntryPassGiftDiscount } from '../../../../../../../models/entry-pass';
import { EntryPassGiftDiscountType, EntryPassStatus } from '../../../../../../../models/entry-pass-enums';
import { EntryPassService } from '../../../../../../../services/entry-pass/entry-pass.service';


@Component({
  selector: 'app-entry-pass-gift-discount-detail',
  templateUrl: './entry-pass-gift-discount-detail.component.html',
  styleUrls: ['./entry-pass-gift-discount-detail.component.css']
})
export class EntryPassGiftDiscountDetailComponent implements OnInit, ComponentHasUnsavedChanges {
  public isNewDiscount: boolean;
  public discount: EntryPassGiftDiscount = {
    id: null,
    url: null,
    is_automatic: false,
    code: '',
    name: '',
    discount_type: EntryPassGiftDiscountType.currency,
    amount: null,
    start_date: null,
    end_date: null,
    entry_pass_ids: [],
    entry_pass_names: [],
    created_at: null,
    updated_at: null,
    last_updated_by_id: null,
    last_updated_by_first_name: null,
    last_updated_by_last_name: null,
    last_updated_by_email: null,
  }
  public entryPasses: EntryPass[];

  // Used to ensure retired products are included as options if they are
  // already on the discount.
  // Otherwise, retired products are not shown.
  public initialProductIds: number[] = [];

  public unsavedChanges = false;
  public errorMessage = '';
  public errors: any = {};

  public loadingExistingDiscount = false;
  public loadingEntryPasses = true;
  public loadingSubmit = false;
  public loadingDelete = false;

  public discountNotFound = false;
  public serverError: number = null;

  public myAccount: MyAccount;

  public EntryPassGiftDiscountType = EntryPassGiftDiscountType;
  public EntryPassStatus = EntryPassStatus;

  constructor(
    public orgConfig: OrganizationConfigService,
    private route: ActivatedRoute,
    private router: Router,
    private entryPassService: EntryPassService,
    private accountService: AccountService,
    private modalService: NgbModal,
  ) {}

  ngOnInit() {
    const routeParams = this.route.snapshot.params;
    this.isNewDiscount = !routeParams.hasOwnProperty('id');

    this.myAccount = this.accountService.getMyAccount();
    if (!this.myAccount.is_organization_admin) { return; }

    // Get the discount to edit, if an ID was provided.
    if (!this.isNewDiscount) {
      const idInt = parseInt(routeParams.id, 10);
      if (isNaN(idInt)) {
        this.discountNotFound = true;
        this.loadingExistingDiscount = false;
        return;
      }

      this.loadDiscount(idInt);
    }

    this.loadEntryPasses();
  }

  hasUnsavedChanges() {
    return this.unsavedChanges;
  }

  @HostListener('window:beforeunload')
  allowUnload() {
    return !this.hasUnsavedChanges();
  }

  public loadDiscount(id: number) {
    this.loadingExistingDiscount = true;

    this.entryPassService
      .getEntryPassGiftDiscount(id)
      .then(discount => {
        this.discount = discount;
        this.initialProductIds = [...this.discount.entry_pass_ids];
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.discountNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingExistingDiscount = false; });
  }

  public loadEntryPasses() {
    this.loadingEntryPasses = true;

    this.entryPassService
      .getEntryPasses(1, 3000, {})
      .then(result => {
        this.entryPasses = result.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.discountNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEntryPasses = false; });
  }

  public handleSubmit() {
    this.loadingSubmit = true;

    this.errorMessage = '';
    this.errors = {};

    let savePromise: Promise<EntryPassGiftDiscount>;
    if (this.isNewDiscount) {
      savePromise = this.entryPassService.addEntryPassGiftDiscount(this.discount);
    } else {
      savePromise = this.entryPassService.updateEntryPassGiftDiscount(this.discount);
    }

    savePromise
      .then(savedDiscount => {
        this.unsavedChanges = false;

        this.discount = savedDiscount;

        let successMessage: string;
        let discountName = this.discount.name;
        if (!discountName) {
          discountName = this.discount.code;
        }
        if (this.isNewDiscount) {
          successMessage = discountName + ' created successfully.';
        } else {
          successMessage = discountName + ' updated successfully.';
        }

        this.router.navigate(
          ['/gifting-config/discounts'],
          {
            state: {
              feedback: {
                text: successMessage,
                status: 'success'
              }
            }
          }
        );
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errors = reason.error;
          this.errorMessage = 'Please correct the errors shown and try again.';
        } else if (reason.status === 403) {
          if (this.isNewDiscount) {
            this.errorMessage = 'You do not have permission to add gift discounts.';
          } else {
            this.errorMessage = 'You do not have permission to modify this gift discount.';
          }
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSubmit = false; });
  }

  public handleDelete() {
    const modalRef = this.modalService.open(SimpleMessageModalComponent);
    modalRef.componentInstance.headerText = 'Delete Discount';
    modalRef.componentInstance.messageText = 'Are you sure you want to delete this discount? This action cannot be undone.';
    modalRef.componentInstance.messageAlertClass = 'alert-danger';
    modalRef.componentInstance.hasConfirmation = true;
    modalRef.componentInstance.confirmedCallback = () => {
      this.loadingDelete = true;

      this.entryPassService
        .deleteEntryPassGiftDiscount(this.discount)
        .then(() => {
          this.unsavedChanges = false;

          let discountName = this.discount.name;
          if (!discountName) {
            discountName = this.discount.code;
          }

          this.router.navigate(
            ['/gifting-config/discounts'],
            {
              state: {
                feedback: {
                  text: discountName + ' deleted.',
                  status: 'success'
                }
              }
            }
          )
        })
        .catch(reason => {
          if (reason.status === 400) {
            this.errors = reason.error;
            this.errorMessage = 'Please correct the errors shown and try again.';
          } else if (reason.status === 403) {
            this.errorMessage = 'You do not have permission to delete this discount.';
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => { this.loadingDelete = false; });
    }
  }

  public getFilteredProducts(): EntryPass[] {
    return this.entryPasses.filter(item => {
      if (this.initialProductIds.includes(item.id)) { return true; }

      return [EntryPassStatus.draft, EntryPassStatus.active].includes(item.status);
    });
  }

  public productSelected(product: EntryPass) {
    return this.discount.entry_pass_ids.includes(product.id);
  }

  public toggleProduct(product: EntryPass) {
    if (this.productSelected(product)) {
      this.discount.entry_pass_ids.splice(this.discount.entry_pass_ids.indexOf(product.id), 1);
    } else {
      this.discount.entry_pass_ids.push(product.id);
    }
    this.unsavedChanges = true;
  }

  public allProductsAreSelected() {
    for (const product of this.getFilteredProducts()){
      if (!this.discount.entry_pass_ids.includes(product.id)) {
        return false;
      }
    }

    return true;
  }

  public setNoProductsSelected() {
    this.discount.entry_pass_ids = [];
    this.unsavedChanges = true;
  }

  public setAllProductsSelected() {
    this.discount.entry_pass_ids = this.getFilteredProducts().map(product => product.id);
    this.unsavedChanges = true;
  }

  public isLoading() {
    return (
      this.loadingExistingDiscount ||
      this.loadingEntryPasses ||
      this.loadingSubmit ||
      this.loadingDelete
    )
  }
}
